// src/pages/tenant/RequestDetail.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Avatar,
  TextField,
  Button,
  Grid
} from '@mui/material';
import { useParams } from 'react-router-dom';
import {
  doc,
  getDoc,
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';

export default function RequestDetail() {
  const { id } = useParams();
  const auth = getAuth();

  const [request, setRequest]       = useState(null);
  const [updates, setUpdates]       = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [tenantInfo, setTenantInfo] = useState(null);

  // load the request & tenant profile
  useEffect(() => {
    const fetchRequestAndUser = async () => {
      const workOrderRef = doc(db, 'workOrders', id);
      const workOrderSnap = await getDoc(workOrderRef);

      if (!workOrderSnap.exists()) {
        console.warn('No such work order:', id);
        return;
      }

      const req = { id: workOrderSnap.id, ...workOrderSnap.data() };
      setRequest(req);

      // grab tenant’s name/email
      const userSnap = await getDoc(doc(db, 'users', req.tenantUid));
      if (userSnap.exists()) {
        setTenantInfo(userSnap.data());
      }
    };

    // subscribe to updates sub-collection
    const updatesQuery = query(
      collection(db, 'workOrders', id, 'updates'),
      orderBy('createdAt', 'asc')
    );
    const unsubscribe = onSnapshot(updatesQuery, snap => {
      setUpdates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    fetchRequestAndUser();
    return () => unsubscribe();
  }, [id]);

  const handleSend = async () => {
    const text = newMessage.trim();
    if (!text) return;

    // use Firebase Auth displayName or fallback to tenantInfo
    const senderName =
      auth.currentUser.displayName ||
      tenantInfo?.name ||
      auth.currentUser.email ||
      'Unknown';

    await addDoc(collection(db, 'workOrders', id, 'updates'), {
      message: text,
      sender: senderName,
      createdAt: serverTimestamp(),
      uid: auth.currentUser.uid
    });

    setNewMessage('');
  };

  if (!request) {
    return <Typography>Loading…</Typography>;
  }

  return (
    <Box p={4} sx={{ display: 'flex' }}>
      <Box flex={1}>
        <Typography variant="h5" mb={2}>
          Maintenance request
        </Typography>

        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 3 }}>
          {request.imageUrl && (
            <Box mt={2}>
              <img
                src={request.imageUrl}
                alt="Attachment"
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  objectFit: 'contain',
                  borderRadius: 8,
                  display: 'block',
                  margin: '0 auto'
                }}
              />
            </Box>
          )}

          <Typography variant="h6">{request.subject}</Typography>
          <Typography mb={2} color="text.secondary">
            {request.description}
          </Typography>

          <Typography variant="body2" mt={1}>
            🏠 Property manager has permission to enter property
          </Typography>
          <Typography variant="body2" mt={1}>
            🐾 Pets on property
          </Typography>
        </Paper>

        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <GridDisplay
              title="Details"
              items={[
                ['Status', request.status],
                ['Number', request.id.slice(0, 8).toUpperCase()],
                ['Created by', tenantInfo?.name || request.tenantName],
                [
                  'Date created',
                  request.createdAt
                    ? request.createdAt.toDate().toLocaleDateString()
                    : '–'
                ],
                ['Category', request.category]
              ]}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <GridDisplay
              title="Shared with"
              items={[
                ['👤', tenantInfo?.name || request.tenantName],
                ['🏢', 'Memphis Passive LLC']
              ]}
            />
          </Grid>
        </Grid>

        <Paper sx={{ p: 3, mt: 3, borderRadius: 3, boxShadow: 3 }}>
          <Typography variant="h6">Updates</Typography>
          <Divider sx={{ my: 2 }} />

          {updates.map((u) => (
            <Box key={u.id} mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Avatar>{u.sender.charAt(0)}</Avatar>
                <Typography fontWeight="bold">{u.sender}</Typography>
                <Typography color="text.secondary" fontSize={13}>
                  {u.createdAt?.toDate().toLocaleString() || 'Just now'}
                </Typography>
              </Box>
              <Typography mt={1}>{u.message}</Typography>
            </Box>
          ))}

          <Divider sx={{ my: 2 }} />

          <TextField
            label="Write an update"
            fullWidth
            multiline
            rows={3}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
          />
          <Button
            variant="contained"
            color="success"
            sx={{ mt: 2, borderRadius: 3 }}
            onClick={handleSend}
          >
            Send
          </Button>
        </Paper>
      </Box>
    </Box>
  );
}

function GridDisplay({ title, items }) {
  return (
    <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
      <Typography variant="h6">{title}</Typography>
      {items.map(([label, value], i) => (
        <Box key={i} display="flex" justifyContent="space-between" mt={1}>
          <Typography color="text.secondary">{label}</Typography>
          <Typography>{value}</Typography>
        </Box>
      ))}
    </Paper>
  );
}